import { useLazyGetFactoriesQuery } from 'app/features/factory/api';
import { useLazyGetProductsQuery } from 'app/features/products/api';
import { useReducer, useEffect, useCallback } from 'react';
import { useLazyGetSuppliersQuery } from '../../features/factory/api';

const INITIAL_STATE = {
  data: {},
  hasMore: true,
  params: {
    page: 1,
    pageSize: 10,
    keywords: ''
  },
  loading: false,
  error: null
};

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        data: action.payload.list,
        pagination: action.payload.pagination,
        hasMore: action.payload.hasMore,
        error: null
      };
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_PARAMS':
      return { ...state, params: { ...state.params, ...action.payload } };
    case 'RESET_DATA':
      return { ...state, data: [], hasMore: true };
    case 'CLEAR_DATA':
      return { ...INITIAL_STATE };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

const SEARCHER_RESOURCES = {
  products: useLazyGetProductsQuery,
  suppliers: useLazyGetSuppliersQuery,
  factories: useLazyGetFactoriesQuery
};

const useResource = (resourceName) => {
  const resource = SEARCHER_RESOURCES[resourceName];
  if (!resource) {
    throw new Error(`Resource ${resourceName} not found`);
  }
  return resource;
};

export default function useSearcher(options) {
  const {
    resourceName,
    resources = SEARCHER_RESOURCES,
    transformParams,
    hasMore,
    parseResponse,
    lazyLoad = false
  } = options;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const resource = useResource(resourceName);
  const [fetch, result] = resource();

  const fetchData = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      /**
       * The Structured of Response should like this
       *
       * data:{
       * list: [],
       * pagination:{}
       *
       * }
       *  */
      const params = transformParams
        ? transformParams(state.params)
        : state.params;
      const response = await fetch(params).unwrap();
      const { data, pagination } = parseResponse
        ? parseResponse(response)
        : response;
      if (lazyLoad) {
        const merged = state.params.page <= 1 ? data : [...state.data, ...data];
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            pagination,
            list: merged,
            hasMore: hasMore(merged, pagination)
          }
        });
        dispatch({
          type: 'UPDATE_PARAMS',
          payload: { page: state.params.page + 1 }
        });
      } else {
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            pagination: pagination,
            list: data
          }
        });
      }
    } catch (error) {
      dispatch({ type: 'FETCH_ERROR', payload: error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetch, resources, resourceName, hasMore, JSON.stringify(state.params)]);

  const updateParams = useCallback(
    (newParams) => {
      dispatch({ type: 'UPDATE_PARAMS', payload: newParams });
      if (lazyLoad) {
        dispatch({ type: 'RESET_DATA' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchData]
  );

  useEffect(() => {
    if (!lazyLoad) {
      fetchData();
    }
    return () => {
      dispatch({ type: 'CLEAR_DATA' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyLoad]);

  return { ...state, result, fetchData, updateParams };
}
